import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { activateCollective } from '../../../services/compositionApi';
import usePanneauAccessible from './usePanneauAccessible';
import './CollectivePanel.css';

// Panneau « Inviter vos proches » (refonte du collectif, phase 1, 2026-10-24 — maquette docs/bookipix-collectif.html).
// Pose sur l'atelier, a droite, derriere un voile : on ne quitte plus l'atelier pour activer le collectif.
// Activer et partager sont deux gestes separes : AUCUN champ e-mail ici.
// Accessibilite : focus sur le premier champ a l'ouverture, rendu a l'element d'origine a la fermeture, Echap ferme,
// tabulation gardee dans le panneau, erreurs sous chaque champ et focus sur le premier champ en erreur.

const aujourdhui = () => new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris' }).format(new Date()); // AAAA-MM-JJ
const demain = () => {
  const d = new Date(`${aujourdhui()}T12:00:00`);
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function CollectiveInvitePanel({ book, onClose, onActivated }) {
  const { t } = useTranslation('collective');
  const panneauRef = useRef(null);
  const champs = { name: useRef(null), deadline: useRef(null) };

  const messageDe = (occasion) => (occasion.trim()
    ? t('panel.messageWithOccasion', { occasion: occasion.trim() })
    : t('panel.messageBase'));

  const [nom, setNom] = useState(book?.owner_name || '');
  const [occasion, setOccasion] = useState(book?.collective_event_title || '');
  const [message, setMessage] = useState(() => book?.collective_message || messageDe(book?.collective_event_title || ''));
  // Le message se reecrit avec l'occasion TANT QUE l'utilisateur ne l'a pas modifie lui-meme.
  const [messageTouche, setMessageTouche] = useState(Boolean(book?.collective_message));
  const [dateLimite, setDateLimite] = useState(book?.collective_deadline || '');
  // Relances automatiques reellement branchees (phase 5, option A) : proposees par defaut a l'activation.
  const [relances, setRelances] = useState(book?.collective_activated_at ? Boolean(book?.collective_reminders_enabled) : true);
  const [erreurs, setErreurs] = useState({});
  const [erreurServeur, setErreurServeur] = useState('');
  const [enCours, setEnCours] = useState(false);
  usePanneauAccessible(panneauRef, onClose, champs.name);

  const changerOccasion = (valeur) => {
    setOccasion(valeur);
    if (!messageTouche) setMessage(messageDe(valeur));
  };

  const valider = () => {
    const e = {};
    if (nom.trim().length < 2) e.name = t('panel.errors.name');
    if (!dateLimite) e.deadline = t('panel.errors.deadlineMissing');
    else if (dateLimite <= aujourdhui()) e.deadline = t('panel.errors.deadlinePast');
    return e;
  };

  const soumettre = async (event) => {
    event.preventDefault();
    const e = valider();
    setErreurs(e);
    setErreurServeur('');
    if (Object.keys(e).length) {
      const premier = ['name', 'deadline'].find((cle) => e[cle]);
      champs[premier]?.current?.focus();
      return;
    }
    setEnCours(true);
    try {
      const livre = await activateCollective(book.id, {
        organizerName: nom.trim(),
        eventTitle: occasion.trim(),
        message: message.trim(),
        deadline: dateLimite,
        remindersEnabled: relances,
        reminderDaysBefore: [7, 2]
      });
      // Le panneau « Mon collectif » prend le relais tout de suite (phase 2) : voir l'atelier.
      onActivated(livre);
    } catch (err) {
      setErreurServeur(err.message || t('panel.errors.generic'));
    } finally {
      setEnCours(false);
    }
  };

  const idTitre = 'collectif-panel-titre';
  return (
    <>
      <button type="button" className="cp-scrim" aria-label={t('panel.close')} onClick={onClose} tabIndex={-1} />
      <div className="cp-panel" role="dialog" aria-modal="true" aria-labelledby={idTitre} ref={panneauRef}>
        <div className="cp-head">
          <div>
            <h2 id={idTitre}>{t('panel.title')}</h2>
            <p>{t('panel.subtitle')}</p>
          </div>
          <button type="button" className="cp-x" onClick={onClose} aria-label={t('panel.close')}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        <form className="cp-body" onSubmit={soumettre} noValidate>
          <label className="cp-field" htmlFor="cp-nom">
            <span>{t('panel.nameLabel')} <em>{t('panel.required')}</em></span>
            <input
              id="cp-nom"
              ref={champs.name}
              className={`cp-input ${erreurs.name ? 'is-err' : ''}`}
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              maxLength={80}
              autoComplete="name"
              placeholder={t('panel.namePlaceholder')}
              aria-invalid={Boolean(erreurs.name)}
              aria-describedby={erreurs.name ? 'cp-nom-err' : undefined}
            />
            {erreurs.name && <span className="cp-err" id="cp-nom-err">{erreurs.name}</span>}
          </label>

          <label className="cp-field" htmlFor="cp-occ">
            <span>{t('panel.occasionLabel')} <em>{t('panel.optional')}</em></span>
            <input id="cp-occ" className="cp-input" value={occasion} onChange={(e) => changerOccasion(e.target.value)} maxLength={120} placeholder={t('panel.occasionPlaceholder')} />
          </label>

          <label className="cp-field" htmlFor="cp-msg">
            <span>{t('panel.messageLabel')}</span>
            <textarea id="cp-msg" className="cp-input" rows={4} value={message} maxLength={500} onChange={(e) => { setMessage(e.target.value); setMessageTouche(true); }} aria-describedby="cp-msg-aide" />
            <span className="cp-hint" id="cp-msg-aide">{t('panel.messageHint')}</span>
          </label>

          <label className="cp-field" htmlFor="cp-dl">
            <span>{t('panel.deadlineLabel')} <em>{t('panel.required')}</em></span>
            <input
              id="cp-dl"
              ref={champs.deadline}
              type="date"
              className={`cp-input ${erreurs.deadline ? 'is-err' : ''}`}
              value={dateLimite}
              min={demain()}
              onChange={(e) => setDateLimite(e.target.value)}
              aria-invalid={Boolean(erreurs.deadline)}
              aria-describedby={erreurs.deadline ? 'cp-dl-err' : undefined}
            />
            {erreurs.deadline && <span className="cp-err" id="cp-dl-err">{erreurs.deadline}</span>}
          </label>

          <label className="cp-check">
            <input type="checkbox" checked={relances} onChange={(e) => setRelances(e.target.checked)} />
            <span>
              {t('panel.remindersLabel')}
              <small>{t('panel.remindersHonest')}</small>
            </span>
          </label>

          {erreurServeur && <p className="cp-err" role="alert">{erreurServeur}</p>}
          <button type="submit" className="btn btn-primary cp-block" disabled={enCours}>
            {enCours ? t('panel.activating') : t('panel.activate')}
          </button>
        </form>
      </div>
    </>
  );
}
